import { PropType } from 'vue';
import { mapGetters } from 'vuex';
import plantsClient from '@/core/services/plants';
import { Vue, Options } from 'vue-class-component';
import {
  IRoomDTO,
  PlantDetailDTO,
  RoomObjectDTO,
  RoomObjectType,
} from '@/../client-generator/generated-clients/plants';
import { getRoomIconFromType } from '@/core/utils/utils';
import { SET_APP_LOADING } from '@/core/store/action-types';
import { NamingService } from '@/core/services/naming.service';
import RoomObjectGroup from './RoomObjectGroup/RoomObjectGroup.vue';
import DialogConfigureRoom from './DialogConfigureRoom/DialogConfigureRoom.vue';
import { PlantEntityType } from '@/../client-generator/generated-clients/configurations';
import DialogConfirmDeleteRoom from './DialogConfirmDeleteRoom/DialogConfirmDeleteRoom.vue';

@Options({
  name: 'PlantRoom',
  emits: ['add-object', 'add-structure', 'update-list'],
  props: {
    currentPlant: {
      type: Object as PropType<PlantDetailDTO>,
      required: false,
    },
  },
  components: {
    RoomObjectGroup,
    DialogConfigureRoom,
    DialogConfirmDeleteRoom,
  },
  computed: {
    ...mapGetters('UI', [
      'roomEditEnabled',
      'roomConfigEnabled',
      'roomObjectDeleteEnabled',
    ]),
  },
})
export default class PlantRoom extends Vue {
  currentPlant!: PlantDetailDTO;
  isVisibleDeleteRoomConfirm = false;
  isVisibleDialogAssignObject = false;
  isVisibleDialogConfigureRoom = false;
  getRoomIconFromType = getRoomIconFromType;

  get roomObjects() {
    let objects: RoomObjectDTO[] = [];

    if (
      this.currentRoom?.roomObjects &&
      this.currentRoom.roomObjects.length > 0
    ) {
      objects = this.currentRoom?.roomObjects
        .filter((x) => x.type != RoomObjectType.Alarm)
        .sort((a, b) => a.type! - b.type!);
    }

    return objects;
  }

  get hasObjects() {
    if (this.roomObjects) {
      return this.roomObjects?.length > 0;
    }

    return false;
  }

  get currentRoom(): IRoomDTO {
    return (
      this.currentPlant?.rooms?.find(
        (x) => x.id?.toString() === this.$route.params.roomId
      ) ?? {}
    );
  }

  get roomId() {
    return this.$route.params.roomId as string;
  }

  get backLink() {
    return {
      name: 'page-plant-dashboard',
      params: this.$route.params,
    };
  }

  get countConfigurationToEnd() {
    return this.roomObjects.filter((k) => k.hasConfigurationToEnd).length;
  }

  mounted() {
    if (this.$route.query.refresh) {
      this.$emit('update-list');
      this.$router.push({ name: 'page-plant-room' });
    }
  }

  async toggleAddRoomObjectDialog() {
    this.$store.dispatch(SET_APP_LOADING, true);
    await NamingService.prefetchFlatEntities(
      this.currentPlant.desktopId as any,
      PlantEntityType.ROOM_OBJECT
    );
    this.$store.dispatch(SET_APP_LOADING, false);
    this.$emit('add-object', this.currentRoom);
  }

  async toggleConfigureRoomDialog(doUpdate: boolean) {
    if (this.isVisibleDialogConfigureRoom) {
      this.isVisibleDialogConfigureRoom = false;
      this.$store.dispatch(SET_APP_LOADING, false);
    } else {
      NamingService.flushCache();
      this.$store.dispatch(SET_APP_LOADING, true);
      await NamingService.prefetchFlatEntities(
        +this.currentPlant.desktopId!,
        PlantEntityType.ROOM
      );
      this.isVisibleDialogConfigureRoom = true;
    }
  }

  toggleDeleteRoomConfirm(doUpdate: boolean) {
    this.isVisibleDeleteRoomConfirm = !this.isVisibleDeleteRoomConfirm;
  }

  async deleteRoom() {
    if (
      !this.currentPlant ||
      !this.currentPlant.desktopId ||
      !this.currentRoom.id
    )
      return;
    try {
      this.$store.dispatch(SET_APP_LOADING, true);
      await plantsClient.roomsDELETE(
        this.currentPlant.desktopId,
        this.currentRoom.id
      );
      this.$store.dispatch(SET_APP_LOADING, false);
      this.$emit('update-list');
      this.$router.push({ name: 'page-plant-dashboard' });
    } catch (error) {
      switch (error.status) {
        case 423:
          this.$router.push({
            name: 'page-plants-hub',
          });
          break;
      }
      console.log(error);
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  async handleDeleteObject(object: RoomObjectDTO) {
    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      await plantsClient.deleteRoomObject(
        this.currentPlant.desktopId as number,
        this.roomId,
        object.id ?? ''
      );

      this.$emit('update-list');
      this.$router.push({ name: 'page-plant-room' });
    } catch (error) {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 3000,
      });
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }
}
